import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classnames from 'classnames';
import { trackEvent } from 'dibs-tracking';
import { createFragmentContainer, graphql } from 'react-relay';

import ArrowLeft from 'dibs-icons/exports/ArrowLeft';
import ArrowRight from 'dibs-icons/exports/ArrowRight';
import MessageBannerItem from './MessageBannerItem';
import { getMessagesToShow, getPromoMessage } from './messageHelpers';

import style from './MessageBanner-style.scss';

import {
    isPreview,
    wasShown,
    getTrackingObj,
    setValuesToStorage,
    addToShownList,
    getMessageTitle,
    getAllPreviewMessagesToShow,
} from './messageHelpers';

const NEXT_MESSAGE_INTERVAL = 5000;

class MessageBannerCarousel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            messagesToShow: [],
            curr: 0,
        };

        this.handlePrevious = this.handlePrevious.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.fireTracking = this.fireTracking.bind(this);

        this.messageInterval = null;
    }

    componentDidMount() {
        const { viewer, user, messages, showAllMessages, onMessageDisplay } = this.props;

        let messagesToShow = getPromoMessage(viewer, user);
        if (!messagesToShow) {
            messagesToShow = showAllMessages
                ? getAllPreviewMessagesToShow(messages)
                : getMessagesToShow(messages);
        }

        this.setState({ messagesToShow });

        onMessageDisplay({ messageTitle: getMessageTitle(messagesToShow[0]) });

        // If it is not a preview mode and there are multiple messages it will start auto message loop.
        if (!showAllMessages && messagesToShow.length > 1) {
            this.messageInterval = setInterval(this.handleNext, NEXT_MESSAGE_INTERVAL);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { onMessageDisplay } = this.props;
        const { curr, messagesToShow } = this.state;

        if (curr !== prevState.curr) {
            onMessageDisplay({ messageTitle: getMessageTitle(messagesToShow[curr]) });
        }
    }

    componentWillUnmount() {
        if (this.messageInterval) {
            clearInterval(this.messageInterval);
        }
    }

    handlePrevious() {
        let index = this.state.curr - 1;

        if (index < 0) {
            index = this.state.messagesToShow.length - 1;
        }

        this.fireTracking(this.state.curr, 'previous', index);
        this.setState({ curr: index });
    }

    handleNext() {
        let index = this.state.curr + 1;

        if (index > this.state.messagesToShow.length - 1) {
            index = 0;
        }

        this.fireTracking(this.state.curr, 'next', index);
        this.setState({ curr: index });
    }

    fireTracking(previous, type, current) {
        if (!isPreview() && !this.messageInterval) {
            trackEvent(
                getTrackingObj({
                    messageItem: this.state.messagesToShow[previous],
                    action: `banner ${type} clicked`,
                })
            );
        }
        this.setItemValuesToStorage(this.state.messagesToShow[current]);
    }

    setItemValuesToStorage(messageItem) {
        if (!isPreview() && !wasShown(messageItem)) {
            setValuesToStorage(messageItem);
            addToShownList(messageItem);

            trackEvent(
                getTrackingObj({
                    messageItem,
                    action: 'banner displayed',
                })
            );
        }
    }

    render() {
        const { showAllMessages } = this.props;
        const { curr, messagesToShow } = this.state;
        const count = messagesToShow.length;
        const hasPagination = showAllMessages && count > 1;

        const carouselItemsStyle = {
            width: `${count * 100}%`,
            transform: `translateX(${-(curr / count) * 100}%)`,
        };

        const carouselItemsClasses = classnames(style.carouselItems, {
            [style.noTransition]: !!this.messageInterval,
        });

        const wrapperClasses = classnames(style.carouselWrapper, {
            [style.hasPagination]: hasPagination,
        });

        this.setItemValuesToStorage(messagesToShow[0]);

        return (
            <div className={wrapperClasses}>
                {hasPagination ? (
                    <div className={style.carouselArrowWrapperLeft} onClick={this.handlePrevious}>
                        <ArrowLeft className={style.carouselArrowLeft} />
                    </div>
                ) : null}
                <div className={style.carousel}>
                    <div className={carouselItemsClasses} style={carouselItemsStyle}>
                        {messagesToShow.map((item, index) => (
                            <MessageBannerItem
                                key={`${item.note.serviceId}|${index}`}
                                messageItem={item}
                            />
                        ))}
                    </div>
                </div>
                {hasPagination ? (
                    <div className={style.carouselArrowWrapperRight} onClick={this.handleNext}>
                        <ArrowRight className={style.carouselArrowRight} />
                    </div>
                ) : null}
            </div>
        );
    }
}

MessageBannerCarousel.defaultProps = {
    showAllMessages: false,
    onMessageDisplay: () => {},
};

MessageBannerCarousel.propTypes = {
    viewer: PropTypes.object,
    user: PropTypes.object,
    messages: PropTypes.array,
    showAllMessages: PropTypes.bool,
    onMessageDisplay: PropTypes.func,
};

export default MessageBannerCarousel = createFragmentContainer(MessageBannerCarousel, {
    viewer: graphql`
        fragment MessageBannerCarousel_viewer on Viewer {
            ...messageHelpers_viewer
        }
    `,
    user: graphql`
        fragment MessageBannerCarousel_user on User {
            ...messageHelpers_user
        }
    `,
    messages: graphql`
        fragment MessageBannerCarousel_messages on MessageSetModule @relay(plural: true) {
            userType
            items {
                impressionCap
                note {
                    serviceId
                    content {
                        title
                        message
                        cta {
                            text
                            url
                        }
                    }
                    anchors {
                        serviceId
                        content {
                            title
                            message
                            cta {
                                text
                                url
                            }
                        }
                    }
                }
            }
        }
    `,
});
